import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  Button as MuiButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useEffect, useMemo, useState } from "react";
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldPath,
  useForm,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CMultiSelect } from "@/components/Select";
import { SearchConditionCard } from "@/components/SearchConditionCard";
import { CTextField } from "@/components/TextField/CTextField";
import { CDateRangePicker } from "@/components/DatePicker/CDateRangePicker";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { PREFECTURES, getPrefectureIdByName } from "@/utils/prefecture";
import { useCityWithExternalState } from "@/hooks/useCity";
import { type CityListResponse } from "@/types/city";
import {
  type StoredPictureSearchFormData,
  storedPictureSearchConditionSchema,
  storedPictureSearchDefaultValues,
  MATCH_TYPE_OPTIONS,
  STATUS_OPTIONS,
  SEARCH_CONDITION_HINT_MESSAGE,
} from "./storedPictureSearchConditionSchema";

/**
 * 選択中の市区町村を、選択中の都道府県の候補に含まれるものだけに絞る（別都道府県の残留を防ぐ）。
 * 未取得の都道府県が残る間は復元値を誤って消さないよう保ち、全県の候補が分かった時点で除く。
 * @param selectedCities 選択中の市区町村名
 * @param availableCityNames 選択中の都道府県の市区町村名（取得済みの県分）
 * @param allPrefecturesLoaded 選択中の全都道府県の候補を取得済みか
 * @returns 残す市区町村名
 */
export const pruneCitiesToSelectedPrefectures = (
  selectedCities: string[],
  availableCityNames: ReadonlySet<string>,
  allPrefecturesLoaded: boolean
): string[] =>
  allPrefecturesLoaded
    ? selectedCities.filter((city) => availableCityNames.has(city))
    : selectedCities;

/**
 * 市区町村一覧を都道府県コードごとの市区町村名に振り分ける。
 * 同名の市区町村（府中市＝東京都/広島県 等）も都道府県ごとに別々に保持する。
 * @param cities 市区町村APIの一覧
 * @param prefCodes 取得対象の都道府県コード（先頭0なし。例 "1"＝北海道）
 * @returns 都道府県コード→市区町村名の集合。0件の県も空集合で含める
 */
export const groupCityNamesByPrefectureCode = (
  cities: CityListResponse["list"],
  prefCodes: string[]
): Map<string, Set<string>> => {
  const result = new Map(prefCodes.map((code) => [code, new Set<string>()]));
  for (const city of cities) {
    // APIは北海道等の先頭0を省略する（札幌市中央区=1101）ため、5桁に揃えてから県を判定する。
    const code = String(Number(city.cityCode.padStart(5, "0").slice(0, 2)));
    result.get(code)?.add(city.name);
  }
  return result;
};

/** 都道府県マルチセレクトの選択肢（複数選択のため「指定なし」は置かない） */
const PREFECTURE_OPTIONS = PREFECTURES.map((prefecture) => ({
  value: prefecture.name,
  label: prefecture.name,
}));

/**
 * 一致方式（部分/完全/前方/後方）を選ぶラジオ。既定は「部分一致」。
 * 現状の用途は法人名（companyNameMatchType）のみ。他項目（会社法人等番号=完全一致固定、
 * 住所=前方/部分一致固定）へは適用しない。将来の項目追加を見越して name を受ける汎用実装にしてある。
 */
const MatchTypeRadioGroup: React.FC<{
  control: Control<StoredPictureSearchFormData>;
  name: FieldPath<StoredPictureSearchFormData>;
}> = ({ control, name }) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <RadioGroup
        row
        value={field.value}
        onChange={field.onChange}
        sx={{ mt: 0.125, gap: 0, flexWrap: "wrap", minHeight: 20 }}
      >
        {MATCH_TYPE_OPTIONS.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" sx={{ p: 0.25 }} />}
            label={
              <Typography variant="caption" color="text.secondary">
                {option.label}
              </Typography>
            }
            sx={{ mr: 1, ml: 0 }}
          />
        ))}
      </RadioGroup>
    )}
  />
);

interface Props {
  /** 検索ボタン押下時（バリデーション通過後）に検索条件を受け取る */
  handleSearch: (condition: StoredPictureSearchFormData) => void;
  /** クリアボタン押下時。フォームは本コンポーネント内でリセット済み */
  handleClear: () => void;
  /** 検索中はボタンを非活性にする */
  isLoading: boolean;
  /** 復元時の初期値（詳細画面から戻った際の検索条件）。未指定なら全項目未入力で開始する */
  initialValues?: StoredPictureSearchFormData;
}

type AcquiredDateRangeError =
  FieldErrors<StoredPictureSearchFormData>["acquiredDateRange"];

/**
 * 取得日（範囲）に関するエラーメッセージを、範囲全体・開始日・終了日の順に重複なく集める
 * @param error React-Hook-Formの取得日フィールドのエラー
 * @returns 表示するメッセージ
 */
export const collectAcquiredDateRangeErrorMessages = (
  error: AcquiredDateRangeError
): string[] => {
  if (error === undefined) return [];
  const messages = [error.message, error[0]?.message, error[1]?.message];
  const specified = messages.filter(
    (message): message is string => typeof message === "string"
  );
  return Array.from(new Set(specified));
};

/**
 * 取得済商業登記の検索条件フォーム。
 * 法人名・会社法人等番号・所在地・取得日（期間）のいずれも任意で、指定した条件はすべて満たす（AND）
 */
export const StoredCommercialPictureSearchCondition: React.FC<Props> = (
  props
) => {
  const { handleSearch, handleClear, isLoading, initialValues } = props;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<StoredPictureSearchFormData>({
    // 詳細画面から戻った際は保存済みの条件（initialValues）で初期化して表示を復元する
    defaultValues: initialValues ?? storedPictureSearchDefaultValues,
    resolver: zodResolver(storedPictureSearchConditionSchema),
  });

  // 市区町村セレクトを都道府県（複数選択）に連動させる。選択中の各都道府県から pref_code を引き、
  // 既存の市区町村API（GET /general/city?pref_code=）で該当市区町村を取得する（複数県はまとめて取得）
  const selectedPrefectures = useWatch({ control, name: "prefectures" });
  // 同じ県の組合せは同じキーにし、入力欄の再描画でも参照を安定させる。
  const prefectureKey = selectedPrefectures
    .map((name) => getPrefectureIdByName(name))
    .filter((id): id is number => id != null)
    .sort((a, b) => a - b)
    .join(",");
  const prefCodes = useMemo(
    () => (prefectureKey === "" ? [] : prefectureKey.split(",")),
    [prefectureKey]
  );
  const {
    cityList,
    isCityListReady,
    hasCityListError,
    isCityListValidating,
    retryCityList,
  } = useCityWithExternalState(prefCodes);

  const [cachedCitiesByPrefecture, setCachedCitiesByPrefecture] = useState(
    () => new Map<string, Set<string>>()
  );
  const currentCitiesByPrefecture = useMemo(() => {
    if (!isCityListReady || cityList === null) return null;
    return groupCityNamesByPrefectureCode(cityList.list, prefCodes);
  }, [cityList, isCityListReady, prefCodes]);

  useEffect(() => {
    if (currentCitiesByPrefecture === null) return;
    setCachedCitiesByPrefecture(
      (previous) => new Map([...previous, ...currentCitiesByPrefecture])
    );
  }, [currentCitiesByPrefecture]);

  const { availableCityNames, allPrefecturesLoaded } = useMemo(() => {
    const names = new Set<string>();
    let allLoaded = true;
    for (const code of prefCodes) {
      // 新しい応答は保存effectを待たず優先する。成功0件も空Setで置換する。
      const cities =
        currentCitiesByPrefecture?.get(code) ??
        cachedCitiesByPrefecture.get(code);
      if (cities === undefined) allLoaded = false;
      else cities.forEach((name) => names.add(name));
    }
    return { availableCityNames: names, allPrefecturesLoaded: allLoaded };
  }, [prefCodes, currentCitiesByPrefecture, cachedCitiesByPrefecture]);

  const selectedCities = useWatch({ control, name: "cities" });
  // 所属が確認済みなら、新しい組合せの候補取得を待たず検索できる。
  const canSubmitCities = selectedCities.every((name) =>
    availableCityNames.has(name)
  );
  const cityOptions = useMemo(
    () =>
      Array.from(new Set([...availableCityNames, ...selectedCities])).map(
        (name) => ({ value: name, label: name })
      ),
    [availableCityNames, selectedCities]
  );

  useEffect(() => {
    const kept = pruneCitiesToSelectedPrefectures(
      selectedCities,
      availableCityNames,
      allPrefecturesLoaded
    );
    if (kept.length !== selectedCities.length) setValue("cities", kept);
  }, [selectedCities, availableCityNames, allPrefecturesLoaded, setValue]);

  const acquiredDateRangeErrorMessages = collectAcquiredDateRangeErrorMessages(
    errors.acquiredDateRange
  );

  const onClear = (): void => {
    reset(storedPictureSearchDefaultValues);
    handleClear();
  };

  return (
    <SearchConditionCard>
      <form
        onSubmit={(event) => {
          void handleSubmit((formData) => {
            if (isLoading || !canSubmitCities) return;
            handleSearch(formData);
          })(event);
        }}
        aria-label="取得済商業登記の検索条件"
      >
        <Box
          sx={{
            // 横の余白は画面幅に応じて広げる（px固定にしない）
            px: { xs: 2, sm: 3, md: 4 },
            mt: 3,
            mb: 2.5,
            // 高さは size="small" 本来の値（画面幅非依存）に任せ、テキストの縦中央揃えを崩さない。
            // px指定での高さ固定はしない。フォントと補助文・チップだけ調整する
            "& .MuiInputBase-root": { fontSize: "0.875rem" },
            "& .MuiChip-root": { height: 24, fontSize: "0.75rem" },
            "& .MuiInputAdornment-root .MuiIconButton-root": { p: 0.5 },
            "& .MuiInputLabel-root": { fontSize: "0.8125rem" },
            "& .MuiFormHelperText-root": {
              mx: 0,
              mt: 0.5,
              fontSize: "0.6875rem",
              lineHeight: 1.3,
            },
          }}
        >
          <Grid
            container
            columnSpacing={2}
            rowSpacing={2.5}
            alignItems="flex-start"
          >
            {/* 1段目：法人の識別条件＋取得状況＋取得日 */}
            <Grid item xs={12} sm={6} md={3}>
              <CTextField
                control={control}
                name="companyName"
                id="stored-commercial-picture-company-name"
                label="法人名"
                placeholder="例：株式会社サンプル"
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
              <MatchTypeRadioGroup
                control={control}
                name="companyNameMatchType"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CTextField
                control={control}
                name="corporateNumber"
                id="stored-commercial-picture-corporate-number"
                label="会社法人等番号"
                placeholder="例：0100-01-123456"
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
              <FormHelperText>
                完全一致。ハイフン・空白の有無は問いません
              </FormHelperText>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CMultiSelect
                control={control}
                name="statuses"
                label="取得状況（複数選択可）"
                options={STATUS_OPTIONS}
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
              <FormHelperText>成功／依頼中／失敗あり</FormHelperText>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  "& > *": { flex: 1, minWidth: 0 },
                }}
              >
                <CDateRangePicker<StoredPictureSearchFormData>
                  control={control}
                  name="acquiredDateRange"
                  startDateLabel="開始"
                  endDateLabel="終了"
                  inputFormat="yyyy/MM/dd"
                  mask="____/__/__"
                  size={SizingWrapperStyle.INHERIT}
                  muiSize="small"
                />
              </Box>
              {acquiredDateRangeErrorMessages.length > 0 ? (
                acquiredDateRangeErrorMessages.map((message) => (
                  <FormHelperText key={message} error>
                    {message}
                  </FormHelperText>
                ))
              ) : (
                <FormHelperText>取得日（依頼日）で絞り込みます</FormHelperText>
              )}
            </Grid>

            {/* 本店所在地の見出し（列を崩さないよう、枠で囲まず1行の小見出しにする） */}
            <Grid item xs={12} sx={{ pt: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, lineHeight: 1.25 }}
                >
                  本店所在地
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  商業登記上の所在地で絞り込みます
                </Typography>
              </Box>
            </Grid>

            {/* 2段目：本店所在地（都道府県→市区町村→それ以降）。上段と同じ4カラムに揃える */}
            <Grid item xs={12} sm={6} md={3}>
              <CMultiSelect
                control={control}
                name="prefectures"
                label="都道府県（複数選択可）"
                options={PREFECTURE_OPTIONS}
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CMultiSelect
                control={control}
                name="cities"
                label="市区町村（複数選択可）"
                options={cityOptions}
                isClearable={selectedPrefectures.length > 0}
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
              <FormHelperText error={hasCityListError}>
                {hasCityListError
                  ? "候補を取得できませんでした。再試行するか、市区町村の選択を解除してください"
                  : !canSubmitCities
                  ? "選択した市区町村を確認しています"
                  : selectedPrefectures.length === 0
                  ? "都道府県を選ぶと選択できます"
                  : "選択中の都道府県から選べます"}
              </FormHelperText>
              {hasCityListError && (
                <MuiButton
                  type="button"
                  size="small"
                  onClick={retryCityList}
                  disabled={isCityListValidating}
                >
                  市区町村を再取得
                </MuiButton>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CTextField
                control={control}
                name="addressRest"
                id="stored-commercial-picture-address-rest"
                label="それ以降の住所"
                placeholder="例：丸の内1-1-1"
                size={SizingWrapperStyle.INHERIT}
                muiSize="small"
              />
              <FormHelperText>番地等・部分一致</FormHelperText>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mt: 2,
              pt: 2,
              pb: 0.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <FormHelperText sx={{ m: 0 }}>
              {SEARCH_CONDITION_HINT_MESSAGE}
            </FormHelperText>
            <Box sx={{ display: "flex", gap: 1 }}>
              <MuiButton
                type="button"
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={onClear}
                disabled={isLoading}
              >
                クリア
              </MuiButton>
              <MuiButton
                type="submit"
                variant="contained"
                size="small"
                startIcon={<SearchIcon />}
                disabled={isLoading || !canSubmitCities}
              >
                {isLoading ? "検索中…" : "この条件で検索"}
              </MuiButton>
            </Box>
          </Box>
        </Box>
      </form>
    </SearchConditionCard>
  );
};
